import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUsers,
  updateUser,
  deactivateUser,
  inviteUser,
  acceptInvite,
  getPendingInvites,
  deleteInvite,
  resendInvite,
  getAuditLogs,
  getUserAccess,
  updateUserAccess,
  getRoleDefaults,
  updateRoleDefaults,
  type AccessOverride,
} from '../api/users';

export function useUsers(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  role?: string;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => getUsers(params),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateUser>[1] }) =>
      updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: acceptInvite,
  });
}

export function usePendingInvites() {
  return useQuery({
    queryKey: ['invites'],
    queryFn: getPendingInvites,
  });
}

export function useDeleteInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resendInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export function useAuditLogs(params?: {
  page?: number;
  pageSize?: number;
  action?: string;
  resourceType?: string;
  userId?: string;
}) {
  return useQuery({
    queryKey: ['auditLogs', params],
    queryFn: () => getAuditLogs(params),
  });
}

export function useUserAccess(id: string | undefined) {
  return useQuery({
    queryKey: ['userAccess', id],
    queryFn: () => getUserAccess(id as string),
    enabled: !!id,
  });
}

export function useUpdateUserAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      overrides,
      teamIds,
    }: {
      id: string;
      overrides: Record<string, AccessOverride>;
      teamIds?: string[];
    }) => updateUserAccess(id, overrides, teamIds),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['userAccess', id] });
    },
  });
}

export function useRoleDefaults() {
  return useQuery({
    queryKey: ['roleDefaults'],
    queryFn: getRoleDefaults,
  });
}

export function useUpdateRoleDefaults() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRoleDefaults,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roleDefaults'] });
    },
  });
}
